// src/components/Sienaguard/BuyTokensModal.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Separator } from '../ui/separator';
import { Button } from '../ui/button';
import { CreditCard, Shield, Lock, Check } from 'lucide-react';
import { FaPaypal } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

import { getTokenPrice } from '../../api/pricingService';
import {
  createCheckoutSessionTokens,
  createPaypalTokensOrder,
} from '../../api/paymentService';
import useCurrency from '../../hooks/useCurrency';
import { symbolFor } from '../../utils/getSuggestedCurrency';


type Gateway = 'stripe' | 'paypal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function BuyTokensModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation();

  const { currency, ready } = useCurrency(); // p. ej. 'JPY'
  const curSymbol = symbolFor(currency);

  /* ---------- estado del componente ----------- */
  const [numTokens, setNum] = useState(1);
  const [price, setPrice] = useState<number | null>(null); // precio final
  const [baseline, setBase] = useState<number | null>(null); // precio tachado si >1
  const [gateway, setGW] = useState<Gateway>('stripe');
  const [loading, setLoading] = useState(false);

  /* ---------- recalcular precio al cambiar nº o divisa ---------- */
  useEffect(() => {
    if (!ready) return; // esperamos a conocer la divisa

    // Precio final
    setPrice(null); // spinner
    getTokenPrice(numTokens, currency.toUpperCase())
    .then(r => {
      setPrice(r.price);
    })
    .catch(err => {
      setPrice(numTokens * 24.99);     // fallback EUR
    });

    if (numTokens > 1) {
      getTokenPrice(1, currency.toUpperCase())
        .then((r) => setBase(r.price * numTokens))
        .catch(() => setBase(24.99 * numTokens));
    } else {
      setBase(null);
    }
  }, [numTokens, currency, ready]);

  /* ---------- iniciar compra ---------- */
  const handleBuy = async () => {
    try {
      setLoading(true);
      if (gateway === 'stripe') {
        // Stripe prefiere el código de divisa en minúsculas
        const { url } = await createCheckoutSessionTokens(
          numTokens,
          currency.toLowerCase()
        );
        window.open(url, '_blank');
      } else {
        // PayPal lo quiere en mayúsculas
        const { approve_url } = await createPaypalTokensOrder(
          numTokens,
          currency.toUpperCase()
        );
        window.open(approve_url, '_blank');
      }
    } catch (err) {
      console.error(err);
      alert('Error iniciando el pago');
    } finally {
      setLoading(false);
    }
  };

  /* ---------- helper para formatear ---------- */
  const fmt = (v: number | null) =>
    v === null ? '…' : `${v.toFixed(2)}${curSymbol}`;

  /* ============================================================= */
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-white rounded-lg">
        <div className="grid md:grid-cols-2">
          {/* ------------------- IZQUIERDA ------------------- */}
          <div className="p-6 bg-gradient-to-br from-blue-50 to-white">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">
                {t('sienaguardmodal.title')}
              </DialogTitle>
              <DialogDescription>
                {t('sienaguardmodal.subtitle')}
              </DialogDescription>
            </DialogHeader>

            {/* Beneficios */}
            {[
              {
                icon: Shield,
                title: t('sienaguardmodal.benefits.globalMonitoringTitle'),
                descr: t(
                  'sienaguardmodal.benefits.globalMonitoringDescription'
                ),
              },
              {
                icon: Lock,
                title: t('sienaguardmodal.benefits.annualCoverageTitle'),
                descr: t('sienaguardmodal.benefits.annualCoverageDescription'),
              },
            ].map(({ icon: Icon, title, descr }, i) => (
              <div key={i} className="mt-6 flex space-x-3">
                <Icon className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium">{title}</p>
                  <p className="text-sm text-gray-600">{descr}</p>
                </div>
              </div>
            ))}
          </div>

          {/* ------------------- DERECHA ------------------- */}
          <div className="p-6 bg-white border-l flex flex-col">
            {/* selector cantidad */}
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold mb-2">
                {t('sienaguardmodal.chooseAmount')}
              </h3>
              <div className="flex justify-center items-center space-x-4">
                <button
                  onClick={() => setNum(Math.max(1, numTokens - 1))}
                  className="w-10 h-10 bg-gray-200 rounded-full hover:bg-gray-300"
                >
                  –
                </button>
                <span className="text-4xl font-bold">{numTokens}</span>
                <button
                  onClick={() => setNum(numTokens + 1)}
                  className="w-10 h-10 bg-gray-200 rounded-full hover:bg-gray-300"
                >
                  +
                </button>
              </div>
            </div>

            <Separator />

            {/* precio */}
            <div className="text-center mt-4 mb-6">
              <p className="text-gray-500 text-sm mb-1">
                {t('sienaguardmodal.yourPrice')}
              </p>

              {baseline && (
                <p className="text-3xl line-through text-gray-400">
                  {fmt(baseline)}
                </p>
              )}

              <p className="text-5xl font-extrabold text-blue-700">
                {fmt(price)}
              </p>

              {baseline && price !== null && (
                <p className="text-green-600 text-sm mt-1">
                  {t('sienaguardmodal.savings', {
                    savings:     (baseline - price).toFixed(2),
                    percentage:  Math.round(((baseline - price) / baseline) * 100),
                    sym:         curSymbol          // ←  nuevo
                  })}
                </p>
              )}
            </div>

            {/* botón COMPRAR */}
            <div className="px-6 pb-2">
              <Button
                disabled={loading || price === null}
                onClick={handleBuy}
                className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
              >
                <Check className="mr-2 h-5 w-5" />
                {t('sienaguardmodal.buyNow')}
              </Button>
            </div>

            {/* pasarela */}
            <div className="flex justify-center space-x-6 mt-4 px-6">
              {(['stripe', 'paypal'] as const).map((g) => (
                <button
                  key={g}
                  onClick={() => setGW(g)}
                  className={`p-3 rounded-lg border transition ${
                    gateway === g
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-300'
                  }`}
                >
                  {g === 'stripe' ? (
                    <CreditCard className="h-6 w-6 text-gray-700" />
                  ) : (
                    <FaPaypal className="h-6 w-6 text-gray-700" />
                  )}
                </button>
              ))}
            </div>

            {/* footer */}
            <div className="flex flex-col items-center text-gray-500 space-y-2 pb-4 mt-4">
              <span className="text-xs">
                {t('sienaguardmodal.securePayment')}
              </span>
              <button
                onClick={onClose}
                className="underline text-xs hover:text-gray-700"
              >
                {t('sienaguardmodal.close')}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
